import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Wallet, Plus, AlertTriangle, ShieldAlert } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export function LeadWalletCard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [disputeId, setDisputeId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const { data: wallet } = useQuery({
    queryKey: ['lead-wallet', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('lead_wallet' as any).select('*').eq('user_id', user!.id).maybeSingle();
      return data as any;
    },
  });

  const { data: charges } = useQuery({
    queryKey: ['lead-charges', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('lead_charges' as any).select('*').eq('user_id', user!.id)
        .order('created_at', { ascending: false }).limit(10);
      return (data as any[]) || [];
    },
  });

  const balance = Number(wallet?.balance_etb || 0);
  const threshold = Number(wallet?.low_balance_threshold || 500);
  const lowBalance = balance < threshold;

  const updateAlert = async (newThreshold: number) => {
    await supabase.from('lead_wallet' as any).upsert({
      user_id: user!.id, low_balance_threshold: newThreshold,
    });
    qc.invalidateQueries({ queryKey: ['lead-wallet', user?.id] });
    toast.success('Alert threshold updated');
  };

  const dispute = async () => {
    if (!disputeId || !reason.trim()) return;
    const { error } = await supabase.rpc('dispute_lead_charge' as any, {
      _charge_id: disputeId, _reason: reason,
    });
    if (error) return toast.error(error.message);
    toast.success('Dispute submitted — our team will review.');
    setDisputeId(null);
    setReason('');
    qc.invalidateQueries({ queryKey: ['lead-charges', user?.id] });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary" /> Lead Wallet
          {lowBalance && <Badge variant="destructive" className="gap-1 text-[10px]"><AlertTriangle className="h-3 w-3" /> Low</Badge>}
        </CardTitle>
        <Button asChild size="sm" className="gap-1">
          <Link to="/dashboard/credit-store?tab=leads"><Plus className="h-3.5 w-3.5" /> Top up</Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold tabular-nums">{balance.toLocaleString()}</span>
          <span className="text-sm text-muted-foreground">ETB available</span>
        </div>

        <div className="flex items-center gap-3 text-sm">
          <Label htmlFor="threshold" className="text-xs text-muted-foreground">Low-balance alert at</Label>
          <Input id="threshold" type="number" defaultValue={threshold}
            onBlur={(e) => updateAlert(Number(e.target.value))}
            className="h-8 w-28 text-right" />
          <span className="text-xs">ETB</span>
        </div>

        {charges && charges.length > 0 && (
          <div>
            <p className="text-sm font-semibold mb-2">Recent charges</p>
            <div className="divide-y text-sm">
              {charges.map((c) => (
                <div key={c.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium">-{Number(c.amount_etb).toLocaleString()} ETB</p>
                    <p className="text-xs text-muted-foreground">{format(new Date(c.created_at), 'd MMM yyyy')} · {c.reason || 'Lead'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      c.status === 'charged' ? 'default'
                      : c.status === 'disputed' ? 'secondary'
                      : c.status === 'refunded' ? 'outline'
                      : 'outline'
                    } className="capitalize">{c.status}</Badge>
                    {c.status === 'charged' && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 gap-1"
                        onClick={() => setDisputeId(c.id)}>
                        <ShieldAlert className="h-3 w-3" /> Dispute
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Dialog open={!!disputeId} onOpenChange={(o) => !o && setDisputeId(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Dispute lead charge</DialogTitle></DialogHeader>
            <Label>Why are you disputing this charge?</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. fake number, wrong city" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisputeId(null)}>Cancel</Button>
              <Button onClick={dispute}>Submit dispute</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
